/**
 * Webhook-outbox list (PR-6a — operational observability).
 *
 * Outbound webhook deliveries. State is derived from the projection
 * columns (delivered_at, dead_lettered_at, locked_until):
 *   - pending / in_flight / delivered / dead_lettered.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Send, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { SearchInput } from "@/components/shell/SearchInput";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import {
  getWebhookOutbox,
  type WebhookOutboxRow,
  type WebhookOutboxState,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const STATES: readonly WebhookOutboxState[] = [
  "pending",
  "in_flight",
  "delivered",
  "dead_lettered",
] as const;

const STATE_VARIANT: Record<WebhookOutboxState, BadgeProps["variant"]> = {
  pending: "muted",
  in_flight: "info",
  delivered: "success",
  dead_lettered: "destructive",
};

export function WebhookOutboxListPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const state = (params.get("state") as WebhookOutboxState | null) ?? undefined;
  const event_type = params.get("event_type") ?? undefined;
  const q = params.get("q") ?? undefined;

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["webhook-outbox", { state, event_type, q }] as const,
    queryFn: () => getWebhookOutbox({ state, event_type, q }),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load webhook outbox", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error]);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(state || event_type || q);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<WebhookOutboxRow>>>(
    () => [
      {
        key: "id",
        header: "Outbox row",
        cell: (r) => (
          <code className="font-mono text-xs text-muted-foreground">
            {r.id.slice(0, 14)}…
          </code>
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{r.tenant.name}</span>
            <Badge variant="muted" className="capitalize">
              {r.tenant.environment}
            </Badge>
          </div>
        ),
      },
      {
        key: "event_type",
        header: "Event",
        cell: (r) => <span className="font-mono text-xs">{r.event_type}</span>,
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge variant={STATE_VARIANT[r.state]} className="capitalize">
            {r.state.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        key: "attempts",
        header: "Attempts",
        className: "text-right tabular-nums",
        cell: (r) => <span className="text-sm">{r.attempts}</span>,
      },
      {
        key: "created_at",
        header: "Enqueued",
        className: "text-muted-foreground",
        cell: (r) => (
          <span className="whitespace-nowrap text-xs">
            {new Date(r.created_at).toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Operations / Outbox"
        title="Webhook outbox"
        description="Outbound webhook deliveries. Click a row to see every attempt against the destination."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by dedup key…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
        filters={
          <>
            <FilterGroup label="State">
              <FilterChip
                active={state === undefined}
                onClick={() => setFilter("state", null)}
              >
                All
              </FilterChip>
              {STATES.map((s) => (
                <FilterChip
                  key={s}
                  active={state === s}
                  onClick={() => setFilter("state", s)}
                >
                  {s.replace(/_/g, " ")}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<WebhookOutboxRow>
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/webhook-outbox/${r.id}`)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Send}
              title="No tenants granted yet"
              description="Outbox rows appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Send}
              title="No outbox rows match these filters"
              description="Try clearing a filter. Outbox rows are enqueued as your platform emits webhooks."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/webhook-outbox">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
    </>
  );
}

export default WebhookOutboxListPage;
